import { Divider, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { OrganizationOwnerSelect } from '../Forms/OrganizationOwnerSelect.component';
import { OrganizationDeliveryPointSection } from '../Forms/OrganizationDeliveryPointSection.component';
import { DeliverySummaryDisplay } from '../Forms/DeliverySummaryDisplay.component';
import { AddressDisplayForm } from '../Forms/AddressDisplayForm.component';
import {
  Organization,
  OrganizationAddress,
  OrganizationDeliveryPoint,
} from '../../../organizations/types/organization.type';
import { DeliveryPointAddress } from '../../../delivery-points/types/delivery-point.type';
import { OrganizationOrderOwner } from '../../utils/build-organization-order-payload.util';
import { DeliveryAddress, DeliveryDay } from '../../types/delivery-selection.type';

type Props = {
  organization: Organization;
  selectedOwner: OrganizationOrderOwner | null;
  selectedDeliveryPoint: OrganizationDeliveryPoint | null;
  selectedDay: DeliveryDay | null;
  onOwnerChange: (owner: OrganizationOrderOwner | null) => void;
  onDeliveryPointChange: (deliveryPoint: OrganizationDeliveryPoint | null) => void;
  onDayChange: (day: DeliveryDay | null) => void;
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',

    '& > h4': {
      margin: 0,
      opacity: 0.7,
    },
  },

  paper: {
    padding: theme.spacing(2),
    overflow: 'auto',

    '& > section:first-of-type': {
      display: 'grid',
      gridTemplateColumns: '1fr 2fr',
      gap: '1rem',
      marginBottom: '1rem',
    },
  },

  deliveryAddress: {
    display: 'flex',
    alignItems: 'start',
    justifyContent: 'space-between',
    gap: '1rem',
    marginBottom: '1rem',

    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      width: 350,
      gap: '1rem',

      '& > h4': {
        margin: 0,
      },
    },
  },
}));

function toDisplayAddress(address: OrganizationAddress | DeliveryPointAddress): DeliveryAddress {
  return {
    type: 'home',
    _id: '',
    address: address.street,
    zipCode: address.zipCode,
    city: address.city,
    ...(address.informations && { complement: address.informations }),
  };
}

export function OrganizationDeliveryInfoSection({
  organization,
  selectedOwner,
  selectedDeliveryPoint,
  selectedDay,
  onOwnerChange,
  onDeliveryPointChange,
  onDayChange,
}: Props) {
  const classes = useStyles();

  const showSummary = !!selectedDeliveryPoint && !!selectedDay;

  return (
    <section className={classes.root}>
      <h4>Informations de livraison</h4>
      <Paper className={classes.paper}>
        <section>
          <OrganizationOwnerSelect
            organization={organization}
            selectedOwner={selectedOwner}
            onOwnerChange={onOwnerChange}
          />
          <OrganizationDeliveryPointSection
            deliveryPoints={organization.deliveryPoints ?? []}
            selectedDeliveryPoint={selectedDeliveryPoint}
            selectedDay={selectedDay}
            onDeliveryPointChange={onDeliveryPointChange}
            onDayChange={onDayChange}
          />
        </section>

        {showSummary && (
          <>
            <Divider style={{ marginBottom: '1rem' }} light />
            <DeliverySummaryDisplay deliveryType="organization" deliveryDay={selectedDay} />

            <section className={classes.deliveryAddress}>
              <div>
                <AddressDisplayForm
                  address={toDisplayAddress(selectedDeliveryPoint.address)}
                  onChange={() => undefined}
                  editable={false}
                  label="Adresse de livraison :"
                />
              </div>
              <div>
                <AddressDisplayForm
                  address={toDisplayAddress(organization.address)}
                  onChange={() => undefined}
                  editable={false}
                  label="Adresse de facturation :"
                />
              </div>
            </section>
          </>
        )}
      </Paper>
    </section>
  );
}
